import React, { useEffect, useState } from 'react';
import { presenceApi } from '../../api/presence';
import { subscribeToUserPresence } from '../../api/websocket';
import HrnbCard from '../controls/HrnbCard';
import HrnbButton from '../controls/HrnbButton';
import OnlineStatusIndicator from '../controls/OnlineStatusIndicator';
import '../../css/components/ParticipantCard.css';

const ROLE_LABELS = {
    ORGANIZER: 'Организатор',
    PARTICIPANT: 'Участник',
    JUDGE: 'Жюри',
};

const STATUS_LABELS = {
    PENDING: 'Ожидает',
    APPROVED: 'Одобрен',
    REJECTED: 'Отклонён',
    WITHDRAWN: 'Отозван',
};

const STATUS_COLORS = {
    PENDING: '#ffc107',
    APPROVED: '#28a745',
    REJECTED: '#dc3545',
    WITHDRAWN: '#6c757d',
};

export default function ParticipantCard({ participant, onApprove, onReject, onWithdraw, showActions = false }) {
    const {
        nickname,
        name,
        city,
        role,
        applicationStatus,
        appliedAt,
        userId,
    } = participant;

    // Онлайн-статус
    const [isOnline, setIsOnline] = useState(false);
    const [lastActivity, setLastActivity] = useState(null);

    useEffect(() => {
        if (!userId) return;

        // Загружаем начальный статус
        presenceApi.getStatus(userId)
            .then(data => {
                setIsOnline(data.isOnline);
                setLastActivity(data.lastActivity);
            })
            .catch(err => console.error('[ParticipantCard] Presence error:', err));

        // Подписка на обновления через WebSocket
        const subscription = subscribeToUserPresence(userId, (presence) => {
            setIsOnline(presence.isOnline);
            setLastActivity(presence.lastActivity);
        });

        return () => {
            if (subscription) subscription.unsubscribe();
        };
    }, [userId]);

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Не указано';
        const date = new Date(dateStr);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const roleLabel = ROLE_LABELS[role] || role || 'Не указано';
    const statusLabel = STATUS_LABELS[applicationStatus] || applicationStatus || 'Не указано';
    const statusColor = STATUS_COLORS[applicationStatus] || '#6c757d';
    const displayName = nickname || name || 'Участник';

    return (
        <HrnbCard className="participant-card">
            <div className="participant-card-header">
                <div className="participant-card-info">
                    <h3 className="participant-card-name">
                        {displayName}
                        {userId && (
                            <OnlineStatusIndicator
                                isOnline={isOnline}
                                lastActivity={lastActivity}
                                size="sm"
                                showLabel={false}
                            />
                        )}
                    </h3>
                    {city && (
                        <span className="participant-card-city">{city}</span>
                    )}
                </div>
                <div className="participant-card-badges">
                    <span className="participant-card-role-badge">
                        {roleLabel}
                    </span>
                    <span
                        className="participant-card-status-badge"
                        style={{ backgroundColor: statusColor }}
                        data-status={applicationStatus}
                    >
                        {statusLabel}
                    </span>
                </div>
            </div>

            <div className="participant-card-body">
                <div className="participant-card-detail">
                    <span className="participant-card-detail-label">
                        Дата подачи:
                    </span>
                    <span className="participant-card-detail-value">
                        {formatDate(appliedAt)}
                    </span>
                </div>
            </div>

            {showActions && applicationStatus === 'PENDING' && (
                <div className="participant-card-footer">
                    <HrnbButton
                        variant="success"
                        size="sm"
                        onClick={onApprove}
                    >
                        Одобрить
                    </HrnbButton>
                    <HrnbButton
                        variant="danger"
                        size="sm"
                        onClick={onReject}
                    >
                        Отклонить
                    </HrnbButton>
                    {onWithdraw && (
                        <HrnbButton
                            variant="secondary"
                            size="sm"
                            onClick={onWithdraw}
                        >
                            Отозвать
                        </HrnbButton>
                    )}
                </div>
            )}
        </HrnbCard>
    );
}
